import * as React from "react";
import { makeStyles } from "@material-ui/styles";
import { useDispatch, useSelector } from "react-redux";
import { IconButton } from "@material-ui/core";
import { Create, Feedback } from "@material-ui/icons";

import { ChromeAppWrapperView } from "@util/chrome-extension.util";
import { IReduxState } from "@reducers/root.reducer";
import { PostMessageManager } from "@src/util/post-message.util";
import {
  setActiveViewAutopilotList,
  setActiveViewDay,
  setActiveViewFeed,
  setActiveViewTeamHealth,
} from "@actions/chrome-extension.actions";
import { ChromeNavBackBtn } from "./ChromeNavBackBtn";
import { greys } from "@clockwise/web-commons/src/styles";
import { helpUrl } from "@clockwise/client-commons/src/constants/site";
import { useQuery } from "@apollo/client";
import { ChromeNavEventDocument } from "./__generated__/ChromeNavEvent.generated";
import { getValue } from "@clockwise/web-commons/src/util/errorable.util";
import { eventFromParent } from "@clockwise/client-commons/src/util/event";
import { canReschedule } from "@clockwise/web-commons/src/util/scheduling.util";

const DEFAULT_NAV_HEIGHT = 60;

const useStyles = makeStyles({
  container: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    margin: "0px 0px 1px",
    height: DEFAULT_NAV_HEIGHT,
  },
  icon: {
    color: greys.slate,
  },
});

type Props = {
  orgId: string | undefined | null;
  previousView: ChromeAppWrapperView | null;
  defaultView?: ChromeAppWrapperView;
};

export const ChromeNavEvent = ({ orgId, previousView, defaultView }: Props) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const isWebExtension =
    useSelector((state: IReduxState) => state.webExtension.mode) === "extension";
  const calendarId = useSelector(
    (state: IReduxState) => state.webExtension.activeViewEvent.calendarId,
  );
  const externalEventId = useSelector(
    (state: IReduxState) => state.webExtension.activeViewEvent.externalEventId,
  );

  const isExtensionOpen = useSelector((state: IReduxState) => state.webExtension.webExtensionOpen);

  const { data, loading, error } = useQuery(ChromeNavEventDocument, {
    variables: { orgRelayId: orgId, externalEventId, calendarId },
    fetchPolicy: "no-cache",
    skip: !isExtensionOpen,
  });

  const org = getValue(data?.org, "Org");

  const eventParent = getValue(org?.forceFetchEventParentErrorable);
  const event = eventFromParent(eventParent, calendarId);
  const isPrivateEvent = !!event?.locked;

  const canEditEvent = isWebExtension && event && canReschedule(org?.canScheduleResponse);
  !isPrivateEvent && !loading && !error;

  const onEditEventHovered = () => {
    PostMessageManager.sendEventEditHovered(true);
  };

  const onEditEvent = () => {
    PostMessageManager.goToEventEditPage(externalEventId, calendarId);
  };

  let closeDestination = defaultView || ChromeAppWrapperView.Feed;
  if (previousView && previousView === ChromeAppWrapperView.AutopilotList) {
    closeDestination = ChromeAppWrapperView.AutopilotList;
  }

  const onClose = () => {
    switch (closeDestination) {
      case ChromeAppWrapperView.TeamHealth:
        dispatch(setActiveViewTeamHealth());
        break;
      case ChromeAppWrapperView.AutopilotList:
        dispatch(setActiveViewAutopilotList());
        break;
      case ChromeAppWrapperView.Day:
        dispatch(setActiveViewDay());
        break;
      case ChromeAppWrapperView.Feed:
      default:
        dispatch(setActiveViewFeed());
        break;
    }
  };

  return (
    <div className={classes.container}>
      <ChromeNavBackBtn backDestination={closeDestination} onBack={onClose} />
      <div>
        {canEditEvent && (
          <IconButton
            className={classes.icon}
            aria-label="Edit event"
            title="Edit event"
            onClick={onEditEvent}
            onMouseEnter={onEditEventHovered}
          >
            <Create />
          </IconButton>
        )}
        <IconButton
          className={classes.icon}
          aria-label="Contact support"
          title="Contact support"
          component={"a"}
          href={helpUrl}
          target="_blank"
        >
          <Feedback />
        </IconButton>
      </div>
    </div>
  );
};
